import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Form, Input, Row, App, Typography } from 'antd';
import { checkCriteriaExists, createEvaluationCriteria, getEvaluationCriteriaById, updateEvaluationCriteria } from '../../services/evaluation-criteria';
import { Switch } from 'antd';
import { InputNumber } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import usePermissions from '../../hooks/usePermissions';

const { Title } = Typography;

const { useApp } = App;

const EvaluationCriteriaUpsertPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  
  const navigate = useNavigate();
  const { message } = useApp();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);

  const { canAny } = usePermissions();

  useEffect(() => {
    form.setFieldsValue({
      include_default: true,
    });
    if (!isEdit || !id) return;

    setLoading(true);
    getEvaluationCriteriaById(id)
      .then((evaluationCriteria) => {
        if(!evaluationCriteria){
          message.error('El criterio de evaluación no se encuentra o fue eliminado');
          navigate(`/evaluation-criteria/all`, { replace: true });
        }
        form.setFieldsValue({
          description: evaluationCriteria.description?.trim(),
          score: Number(evaluationCriteria.score),
          tie_breaking_order: evaluationCriteria.tie_breaking_order,
          include_default: evaluationCriteria.include_default,
        });
      })
      .catch(() => {
        message.error('No se pudo cargar el criterio de evaluación');
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit, form]);

  /**
   * Función que se ejecuta cuando se envía el formulario
   */
  const onSubmit = async (values) => {
    try {
      setLoading(true);

      const exists = await checkCriteriaExists(values.description.trim(),id);
      if(exists === true){
        message.warning('Ya existe un criterio de evaluación con la descripción proporcionada');
        return;
      }

      if (isEdit && id) {
        const updateData = {
          description: values.description?.trim(),
          score: Number(values.score),
          tie_breaking_order: Number(values.tie_breaking_order),
          include_default: values.include_default,
        };


        await updateEvaluationCriteria(id, updateData);
        message.success('Criterio de evaluación actualizado correctamente');
      } else {
        const createData = {
          description: values.description?.trim(),
          score: Number(values.score),
          tie_breaking_order: Number(values.tie_breaking_order),
          include_default: values.include_default,
        };

        await createEvaluationCriteria(createData);
        message.success('Criterio de evaluación agregado.');
      }
      navigate(`/evaluation-criteria/all`, { replace: true });
    } catch (err) {
      // Extraer datos del error
      const errorData = err?.response?.data || err?.data || {};
      const errorObj = errorData?.error || {};
      const status = errorObj?.status || err?.response?.status || 500;
      const errorCode = errorObj?.code;
      const errorMessage = errorObj?.message || errorData?.message || err?.message || 'Error al guardar el usuario';
      
      // Si es un error de validación (422), mostrar errores en los campos del formulario
      if (status === 422 && (errorCode === 'VALIDATION_ERROR' || errorObj?.details?.issues)) {
        const issues = errorObj?.details?.issues || errorObj?.issues || [];
        
        console.log('Issues encontrados:', issues);
        
        if (issues.length > 0) {
          // Limpiar errores previos
          form.setFields([]);
          
          const fieldErrors = issues
            .filter((issue) => issue.path && issue.message) 
            .map((issue) => {
              let fieldPath = issue.path;
              
              if (typeof fieldPath === 'string') {
                fieldPath = [fieldPath];
              } else if (!Array.isArray(fieldPath)) {
                fieldPath = [String(fieldPath)];
              }
              
              console.log('Mapeando error para campo:', fieldPath, 'Mensaje:', issue.message);
              
              return {
                name: fieldPath,
                errors: [issue.message || 'Error de validación'],
              };
            });
          
          console.log('Field errors a establecer:', fieldErrors);
          
          if (fieldErrors.length > 0) {
            form.setFields(fieldErrors);
            console.log('Errores establecidos en el formulario');
          }
          
          message.error(errorMessage || 'Por favor, corrige los errores en el formulario');
        } else {
          message.error(errorMessage);
        }
      } else {
        message.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/evaluation-criteria/all');
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Button 
          type="text" 
          icon={<LeftOutlined style={{ fontSize: '24px', strokeWidth: 4 }} />} 
          style={{ 
            padding: 0,
            color: '#BA955E',
            fontSize: '24px',
            fontWeight: '700',
            height: 'auto'
          }}
          onClick={handleBack}
        />
        <Title level={3} style={{ marginBottom: 0 }}>
          {isEdit ? 'Editar criterio de evaluación' : 'Agregar criterio de evaluación'}
        </Title>
      </div>
      <Card loading={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onSubmit}
          autoComplete="off"
        >
          <Row gutter={16}>
            <Col xs={24} md={24}>
              <Form.Item
                label="Criterio de evalaución"
                name="description"
                rules={[
                  { required: true, message: 'El criterio de evaluación es requerido' },
                  { maxLength: 255, message: 'Solo se aceptan máximo 255 caracteres' },
                ]}
              >
                <Input placeholder="Dificultad" allowClear />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                label="Puntuación default"
                name="score"
                rules={[
                  { required: true, message: 'La puntuación es requerida' },
                  { type: 'number', min: 1, max: 100, message: 'Debe ser un número entre 1 y 100' },
                ]}
              >
                <InputNumber placeholder="100" min={1} max={100} precision={0} style={{ width: '100%' }}/>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label="Orden desempate"
                name="tie_breaking_order"
                rules={[
                  { type: 'number', min: 1, message: 'Debe ser un número mayor a 0' },
                ]}
              >
                <InputNumber placeholder="1" precision={0} min={1} style={{ width: '100%' }}/>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label="¿Incluir por default?"
                  name="include_default"
                  valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="end" gutter={12} style={{ marginTop: 24 }}>
            <Col>
              <Button onClick={() => navigate(-1)} disabled={loading}>
                Cancelar
              </Button>
            </Col>
            {canAny(['evaluation_criteria.create', 'evaluation_criteria.update']) && (<Col>
              <Button type="primary" htmlType="submit" loading={loading}>
                {isEdit ? 'Guardar cambios' : 'Guardar'}
              </Button>
            </Col>)}
          </Row>
        </Form>
      </Card>
    </div>
  );
};

export default EvaluationCriteriaUpsertPage;

